/**
 * 관리자 오더관리 목록 페이지 ($j.Pages.Admin.OrderList / body data-page-name="OrderList")
 *
 * 기능 요약
 * ─────────
 *  - DataTables 서버사이드 페이지네이션으로 오더 목록 표시
 *  - 상단 필터 버튼/셀렉트로 검색 조건 구성 → POST /adm/ajax/orders/list
 *  - 행 클릭 시 상세탭(openTab) 펼침 / 팝업(detailOrderOpen) 오픈
 *  - 상단 유틸: 일괄수금완료, 푸시알림, 문자접수, 수주대기, 지정미확인, 환원예정, 환원보류, 엑셀
 *
 * 레거시와의 관계
 * ───────────────
 * 기존 public/js/a-order-list.js 의 전역 함수/전역 상태를 이 클래스로 캡슐화.
 * 단, 블레이드의 인라인 onclick(43개) 과 외부창(iframe `parent.orderDetail_show`)에서
 * 레거시 함수명이 그대로 호출되므로 initialize() 에서 window.* 로 메서드를 "브리지" 한다.
 * (블레이드 HTML 을 대규모로 수정하지 않고 점진 이관하기 위한 타협)
 *
 * 서버값 주입 경로
 * ────────────────
 * 컨트롤러(OrderController::listPageData)가 #page-data JSON 으로 넘기고
 * constructor 가 인스턴스 필드로 읽는다.
 */

import WhanSmsModal from './OrderList/WhanSmsModal';
import Util from '../../Common/Util';

// 목록 테이블(컬럼 렌더/DataTables)과 검색필터(opt 문자열)는 협력사 접수(/p/orders)와 공유한다.
// → $j.F.Order.ListTable / $j.F.Order.SearchOpt (resources/js/F/Order/)
// 이 파일에는 "관리자 전용" 기능만 남는다: 일괄수금완료·푸시·문자접수·환원예정·지정미확인·홀드·신규접수.

export default class OrderList {
    /**
     * 상태칩 라벨 — 프리셋 넷 전부. 어느 것을 눌렀든 "○○만 보는 중 ×" 한 줄로 말한다.
     *
     * 수주대기만 빼놨던 적이 있다 — 그 조건은 진행상태 버튼(대기만 on)에 드러나니까. 하지만
     * 넷이 나란히 있는 줄에서 하나만 칩이 안 뜨면 "이건 안 걸린 건가" 로 읽힌다. 있고 없고가
     * 정보가 되려면 규칙이 한결같아야 한다. 해제(×)도 넷 다 같은 자리에 있는 편이 낫다.
     *
     * 다만 환원예정 칩만은 오래 붙어 있지 않는다 — 옵션버튼을 하나라도 건드리면 그 자리에서
     * 내려간다(releaseWhanMode). 칩이 말하는 것은 "프리셋이 찍어준 그대로다" 이므로, 사용자가
     * 필터를 손댄 뒤에도 남아 있으면 거짓말이 된다.
     */
    static MODE_LABEL = { wait: '수주대기', jijung: '지정미확인', undone: '작업미완료', whan: '환원예정', whanhold: '환원보류' };

    constructor() {
        // ─── 검색/정렬 상태 ───
        // DataTables ajax.data 콜백이 이 값들을 읽어 서버에 전달한다.
        this.sortField   = '';       // 정렬 컬럼 (thead th[data-col])
        this.sortOrder   = 'desc';   // asc | desc
        this.dateType    = 'n';      // n=오더생성기준, d=작업일기준
        this.specialCond = '';       // 특수조건 (1=환원예정, 2=지정미확인, 3=환원보류, 4=작업미완료). 지난달환원은 특수조건 없이 토글만 찍는다
        // 지금 켜져 있는 목록 프리셋. '' | 'wait' | 'jijung' | 'undone' | 'whanhold' | 'whan'
        // (지난달환원은 모드가 아니다 — 눌러도 여기에 안 들어온다. 보이는 토글만 찍고 끝낸다)
        // (specialCond 와 1:1 이 아니다 — 수주대기는 특수조건 없이 필터만 바꾸는 프리셋이고,
        //  환원예정은 반대로 모드가 풀린 뒤에도 specialCond='1' 만 남는다 → releaseWhanMode)
        this.listMode    = '';
        // 지금 기간칸에 들어있는 값이 "프리셋이 써넣은 것" 인지. 프리셋을 벗어날 때
        // 되돌릴지 판단하는 데만 쓴다 — 사용자가 직접 잡은 기간은 남의 프리셋 때문에
        // 사라지면 안 되므로, 이 플래그가 켜져 있을 때만 되돌린다.
        this.presetDates = false;
        this.$table      = null;     // DataTables API 객체 (initDataTable 에서 할당)

        // ─── 오더 DB (상세탭 상태) ───
        // 외부창에서는 window.OrderList 로 노출됨.
        // current_ordernum 은 $j.F.Order.ListTable 이 들고 있고, 아래 getter 로 노출한다.
        this.order_db = [];

        // ─── 서버 계산값 (#page-data — OrderController::listPageData) ───
        const d = JSON.parse(document.getElementById('page-data')?.textContent || '{}');
        this.defDay_st         = d.defDay_st || '';
        this.defDay_en         = d.defDay_en || '';
        this.whan_start_dt     = d.whan_start_dt || '';
        this.whan_end_dt       = d.whan_end_dt || '';
        this.whan_done_st      = d.whan_done_st || '';
        this.whan_done_en      = d.whan_done_en || '';
        this.whan_hold_st      = d.whan_hold_st || '';
        this.whan_hold_en      = d.whan_hold_en || '';
        this.undone_st         = d.undone_st || '';
        this.undone_en         = d.undone_en || '';
        this.managerLoginLevel = parseInt(d.managerLoginLevel || 0, 10);
        this.whanSenderPhones  = Array.isArray(d.whanSenderPhones) ? d.whanSenderPhones : [];   // 환원예정 문자 발신번호

        // 발주자/수주자명 초기값 ('이재일#2831' 규약). 지금은 늘 빈값 — GET 파라미터로 받게 되면 여기서 넣는다.
        this.initialWb = '';
        this.initialWs = '';

        // 차량넘버/접수번호(컬럼 20~21) 접기/펼치기 상태. 현장번호(19)는 항상 노출.
        // 기본값은 펼침. 사용자가 토글한 상태는 localStorage 에 저장되어 다음 방문 시 복원.
        this.detailColsVisible = $j.Common.Store.local.get('adm_orders_detail_cols') !== '0';

        // 환원예정 문자발송 모달 (환원예정 모드에서만 노출되는 버튼이 lazy 로 인스턴스화)
        this.whanSms = null;
    }

    /**
     * app.js 의 $j.loadPageModule 이 인스턴스 생성 후 호출하는 엔트리포인트.
     * 전역 브리지 → DOM/DataTables 초기화 → 이벤트 바인딩 순으로 실행한다.
     * (브리지를 가장 먼저 노출해야 DataTables 렌더 HTML 내부의 onclick 이 바로 동작)
     */
    initialize() {
        this.exposeGlobals();
        this.initPage();
        this.initHandler();
    }

    // ==================== 전역 브리지 (인라인 onclick / 외부창 호환) ====================
    /**
     * 외부 창/iframe·동적 주입 blade 호환을 위한 window.* 브리지.
     *
     * 필터 영역의 인라인 onclick 은 전부 걷어내고 $j.F.Order.SearchOpt.bindFilters 의 위임으로
     * 옮겼다(블레이드엔 data-act 만 남는다). 그래서 그때 필요했던 브리지 14개는 사라졌고,
     * 아래 둘만 남는다 — 우리가 붙일 수 없는 곳에서 부르기 때문이다:
     *   orderDetail_show 별도 창에서 parent.* 로 호출 (레거시 skw_2000_order_infoget.php 등)
     *   OrderList        위와 같음
     */
    exposeGlobals() {
        const self = this;

        // 행 확장 상세의 사진·전화번호 클릭 (예전엔 상세 blade 가 전역 showPic 을 인라인으로 불렀다)
        $j.F.Order.ListTable.bindDetail((ono, n) => self.showPic(ono, n));
        window.orderDetail_show = (val) => self.orderDetailShow(val);
        window.OrderList = {
            // getter 로 래핑하여 내부 상태가 바뀌어도 외부에 실시간 반영
            get order_db()         { return self.order_db; },
            get current_ordernum() { return self.current_ordernum; },
            update_list: (list) => self.updateOrderDb(list),
        };
    }

    // ==================== 페이지 초기화 ====================
    /** 날짜 기본값 세팅 후 DataTables 구성. */
    initPage() {
        $('#d1').val(this.defDay_st);
        $('#d2').val(this.defDay_en);
        this.applyUrlOrderNums();
        this.syncNavHeight();
        this.snapshotInitialFilters(); // 프리셋↔접수목록 토글 복원용 초기 상태 저장
        this.initDataTable();
        this.initDetailColsToggle();
        this.initListJump();
    }

    /**
     * `?n=1234,5678` — 다른 관리자 화면이 "이 오더들을 접수창에서 보라"고 창을 띄운 경우.
     * (-6차 부정발급 모니터링의 근거 오더 → 접수창에서 보기)
     *
     * 값은 인풋에 그대로 넣는다. 검색기준은 인풋에 보이는 텍스트라는 규약이 여기서도 같고,
     * 첫 조회는 어차피 인풋을 읽으므로(currentSearchParams) 별도 검색 호출이 필요 없다.
     * 오더번호 검색은 서버에서 날짜범위까지 덮어쓰므로 기간을 맞춰줄 필요도 없다.
     */
    applyUrlOrderNums() {
        const n = new URLSearchParams(location.search).get('n');
        if (!n) return;
        // 숫자·콤마만 남긴다 (인풋은 사람이 손으로 고칠 수도 있는 칸이라 서버도 다시 거른다)
        $('#n').val(n.replace(/[^0-9,]/g, ''));
    }

    /**
     * 목록 상/하단 이동 플로팅 버튼 — /p/orders 삼성접수의 #scrollArrow 와 같은 장치.
     * 버튼 하나로 ↓(맨 아래) ↔ ↑(맨 위) 를 번갈아 수행한다.
     * 스크롤되는 건 문서가 아니라 목록 홀더이므로 그쪽을 움직인다.
     */
    initListJump() {
        const el = document.getElementById('orderlistTableHolderDiv');
        const $btn = $('#listJump');
        if (!el || !$btn.length) return;

        // 방향은 실제 스크롤 위치에서 정한다 — 상태값으로 들고 있으면 사용자가 손으로
        // 스크롤했을 때 화살표가 반대로 남는다(삼성접수의 atBottom 이 그렇다).
        // jQuery animate 로 움직인다 (scrollTop 을 직접 쓰므로 브라우저 스크롤 설정과 무관).
        $btn.off('click').on('click', () => {
            const atBottom = el.scrollTop >= el.scrollHeight - el.clientHeight - 8;
            $(el).stop(true).animate({ scrollTop: atBottom ? 0 : el.scrollHeight }, 400);
        });

        // 스크롤 위치에 따라 화살표 방향을 맞춘다 (맨 아래면 ↑, 그 외엔 ↓)
        $(el).off('scroll.listJump').on('scroll.listJump', () => this.syncListJump());
        $(window).off('resize.listJump').on('resize.listJump', () => this.syncListJump());
        this.resetListJump();
    }

    /** 목록을 새로 그린 뒤(스크롤 맨 위) 버튼 표시를 초기화한다. */
    resetListJump() {
        this.syncListJump();
    }

    /**
     * 이동 버튼의 노출/방향 갱신.
     * 목록이 한 화면에 다 나오면 숨기고, 맨 아래에 있으면 ↑(맨 위로)로 바꾼다.
     */
    syncListJump() {
        const el = document.getElementById('orderlistTableHolderDiv');
        const $btn = $('#listJump');
        if (!el || !$btn.length) return;
        $btn.prop('hidden', el.scrollHeight <= el.clientHeight + 8);
        const atBottom = el.scrollTop >= el.scrollHeight - el.clientHeight - 8;
        // 유니코드 화살표(↑↓)는 획이 가늘어 유리판 위에서 흐릿했다 — 아이콘 폰트로 그린다
        $btn.html(`<i class="fas fa-chevron-${atBottom ? 'up' : 'down'}"></i>`)
            .attr('title', atBottom ? '맨 위로' : '맨 아래로');
    }

    /**
     * 화면고정 레이아웃(sass pages/_order-list-legacy.scss §3)의 기준값 — 네비바 높이를 실측해 --adm-navh 로 넘긴다.
     * 본문 높이를 calc(100vh - var(--adm-navh)) 로 잡아 "표만 스크롤" 되게 만드는 데 쓰인다.
     * (불러톡 인라인 패널도 같은 변수를 쓴다 — $j.F.Admin.ChatPanel.measureVars)
     */
    syncNavHeight() {
        const nav = document.querySelector('.main-header');
        if (nav) document.body.style.setProperty('--adm-navh', nav.offsetHeight + 'px');
    }

    /**
     * 목록의 세로 스크롤바 폭을 필터영역에 알려준다 (--ol-sb).
     *
     * 필터줄 오른쪽 끝(검색버튼)과 표의 오른쪽 끝을 맞추려면 그 스크롤바 폭만큼 필터를 안으로
     * 들여야 한다. 폭은 OS·브라우저마다 달라(맥 오버레이 0 / 윈도 17 …) 고정값을 쓰면
     * 어딘가에서 반드시 어긋나므로 실제로 재서 넘긴다.
     * 목록은 페이지당 50행이라 스크롤바가 사실상 항상 있어 로드·리사이즈 시점 측정으로 충분하다.
     */
    syncListGutter() {
        const holder = document.getElementById('orderlistTableHolderDiv');
        const filter = document.getElementById('orderFilterArea');
        if (!holder || !filter) return;
        filter.style.setProperty('--ol-sb', (holder.offsetWidth - holder.clientWidth) + 'px');
    }

    /**
     * 페이지 로드 직후(블레이드 기본) 필터 상태를 통째로 저장한다.
     * 프리셋(수주대기·지정미확인·환원보류·환원예정)을 해제할 때 이 스냅샷으로 되돌린다.
     */
    snapshotInitialFilters() {
        const optClasses = {};
        $('[id^="opt_"]').each(function () { optClasses[this.id] = $(this).attr('class') || 'flink'; });
        const groupChecks = {};
        $('[id^="tabChbox"]').each(function () { groupChecks[this.id] = $(this).is(':checked'); });
        this._initFilters = {
            optClasses,
            groupChecks,
            optall: $('#optall').attr('class') || 'flink rxon',
            x: $('#x').val(), y: $('#y').val(), z: $('#z').val(), c: $('#c').val(),
            cr: $('#cr').val(), crLabel: $('#cr').data('label') || '',
            d1: $('#d1').val(), d2: $('#d2').val(),
            dateType: this.dateType,
            dt: $('input[name=dt]').val() || 'n',
            topA: $('#topchbox_a').html(),
        };
    }

    /** 저장해둔 초기 상태로 모든 필터를 복원 (프리셋 해제 시 — exitPreset 이 부른다). */
    restoreInitialFilters() {
        const s = this._initFilters;
        if (!s) return;
        Object.keys(s.optClasses).forEach(id => $('#' + id).attr('class', s.optClasses[id]));
        Object.keys(s.groupChecks).forEach(id => $('#' + id).prop('checked', s.groupChecks[id]));
        $('#optall').attr('class', s.optall);
        $('#x').val(s.x); $('#y').val(s.y); $('#z').val(s.z); $('#c').val(s.c);
        $('#cr').val(s.cr).data('label', s.crLabel);
        $j.F.Order.CarDetail.sync();
        $('#d1').val(s.d1); $('#d2').val(s.d2);
        this.dateType = s.dateType;
        $('input[name=dt]').val(s.dt);
        $('#topchbox_a').html(s.topA);
        this.specialCond = '';
        this.presetDates = false;
        $('input[name=w]').val('');
    }

    /**
     * 작업메모 헤더 안의 ▸/◂ 아이콘으로 현장번호/차량넘버/접수번호(컬럼 19~21) 토글.
     * 숨긴 공간만큼 작업메모 컬럼이 자동 확장된다. 상태는 localStorage 에 저장.
     * 헤더 정렬 클릭과 겹치지 않도록 stopPropagation 필수.
     */
    initDetailColsToggle() {
        const self = this;
        const $th = $('#toggle-detail-cols');
        if (!$th.length) return;
        // CSS 클래스 토글 방식. DataTables API 의 visible() 이 이 구성에서 예상대로
        // 동작하지 않아 CSS nth-child 로 직접 display:none 제어.
        // 작업메모 헤더 th 전체가 토글이며, data-col 이 없으므로 정렬 핸들러는 발동하지 않는다.
        const $label = $('#toggle-detail-cols-label');
        const apply = () => {
            $('#order-table').toggleClass('detail-cols-hidden', !self.detailColsVisible);
            // 접힘: 숨겨진 컬럼이 뭔지 힌트 노출 / 펼침: 실제 컬럼이 보이므로 접기 표시만
            $label.text(self.detailColsVisible ? ' ◂ 접기' : ' ▸ 차량/접수');
            $th.attr('title', self.detailColsVisible ? '차량넘버/접수번호 접기' : '차량넘버/접수번호 펼치기');
        };
        apply();
        $th.on('click', function () {
            self.detailColsVisible = !self.detailColsVisible;
            $j.Common.Store.local.set('adm_orders_detail_cols', self.detailColsVisible ? '1' : '0');
            apply();
        });
    }

    /**
     * DataTables 초기화 + 헤더 클릭 정렬 바인딩.
     *
     * 컬럼 렌더/행확장/팝업은 $j.F.Order.ListTable (협력사와 공용) 이 담당한다.
     * 서버사이드 모드이므로 검색/정렬 상태는 인스턴스 필드(sortField/dateType/specialCond)
     * 에 저장해두고, ajax.data 콜백(getSearch)에서 현재 값을 읽어 서버로 보낸다.
     */
    initDataTable() {
        const self = this;

        this.$table = $j.F.Order.ListTable.create({
            selector : '#order-table',
            url      : '/adm/ajax/orders/list',
            detailUrl: '/adm/ajax/order/detail',
            getSearch: () => self.currentSearchParams(),

            // 관리자 전용 렌더 (홀드 input / 회원포털 링크 / 오더핸들 팝업 / 매칭내역 팝업)
            canHold  : true,
            canPortal: true,
            canHandle: true,
            canMatch : true,
            managerLoginLevel: this.managerLoginLevel,

            // 헤더 클릭 정렬: 같은 컬럼 재클릭 시 asc↔desc 토글, 다른 컬럼이면 desc 로 리셋.
            onSort: (col) => {
                if (self.sortField === col) {
                    self.sortOrder = self.sortOrder === 'asc' ? 'desc' : 'asc';
                } else {
                    self.sortField = col;
                    self.sortOrder = 'desc';
                }
                self.$table.ajax.reload();
            },

            // 응답 후처리: 푸터 요약 + 일괄수금완료/푸시발송용 hidden input 구성
            onLoaded: (json) => {
                const tp = json.totalPrice || 0;
                const tc = json.recordsTotal || 0;
                $('#totalPriceInfo').html('총금액 : ' + Util.formatNumber(tp) + '원 (' + Util.formatNumber(tc) + '건)'
                    + $j.F.Order.SearchOpt.memberSearchHtml(json.searchInfo));

                //  - orderNoList   : 콤마 구분 오더번호
                //  - listMemNoLIst : '|' 구분 + 필드 구분자 "'"
                //                    (order_num ' member_num ' 수주자번호 ' 현장주소 ' 작업일)
                const nums = [];
                const memList = [];
                json.data.forEach(function (r) {
                    // 환원예정 모드의 일괄푸시·문자발송 대상 목록에서는 환원보류 오더를 뺀다.
                    // 화면 표(접수목록)는 그대로 다 보여준다 — CS 가 보류 상태를 계속 보고 관리해야 하니까.
                    // 다른 모드(접수목록·수주대기 등)에는 whan_status 가 이 목록을 좌우할 이유가 없어 안 건다.
                    if (self.specialCond === '1' && Number(r.whan_status) === 1) return;
                    nums.push(r.order_num);
                    const soo = r.worker_owner_num || r.worker_num || '';
                    memList.push(r.order_num + "'" + r.order_member_num + "'" + soo + "'" + (r.site_address || '') + "'" + (r.work_day || ''));
                });
                $('#orderNoList').val(nums.join(','));
                $('#listMemNoLIst').val(memList.join('|'));
            },
        });

        this.moveDtFooter();

        // 새로 그려지면 목록을 맨 위로. 표만 스크롤되는 구조라 스크롤이 남아있으면
        // 다음 페이지/새 검색결과가 중간부터 보인다.
        this.$table.on('draw', function () {
            const el = document.getElementById('orderlistTableHolderDiv');
            if (el) el.scrollTop = 0;
            self.resetListJump();
            // 스크롤바는 목록이 그려진 뒤에야 생긴다 — 검색버튼 끝을 표 끝에 맞추려면 여기서 다시 잰다
            self.syncListGutter();
        });
    }

    /**
     * DataTables 가 표 아래(=스크롤 영역 안)에 그린 "건수·페이징" 줄을 하단 고정바로 옮긴다.
     * 표만 스크롤되는 구조라 그냥 두면 한참 내려야 페이징이 보인다.
     *
     * 노드를 그대로 이동시킨다 — DataTables 는 이 노드 참조로 매 draw 마다 내용을 갱신하므로
     * 위치만 바뀌고 동작(페이지 이동·건수 표시)은 그대로다. 생성 직후 1회만 하면 된다.
     */
    moveDtFooter() {
        // 테이블을 다시 만들면($j.reset) 페이징 줄도 새로 생기므로 이전 것을 비우고 담는다
        $('#orderPaging').empty();
        $('#order-table_wrapper .dt-layout-row').has('.dt-paging').appendTo('#orderPaging');
    }

    // ==================== 이벤트 핸들러 초기화 ====================
    /**
     * 셀렉트/자동완성/날짜피커 바인딩. DOM 존재가 전제이므로 initPage() 이후에 호출.
     * 모바일에서는 mobiscroll 데이트피커 대신 브라우저 기본 input 을 쓴다.
     */
    initHandler() {
        const self = this;
        this.checkSerKeys();

        // 창 폭이 바뀌면 네비바가 두 줄로 접힐 수 있다 — 본문 높이 기준값을 다시 잰다
        // (네임스페이스로 걸어 $j.reset 재초기화 시 중복 바인딩을 막는다)
        $(window).off('resize.orderList').on('resize.orderList', () => {
            self.syncNavHeight();
            self.syncListGutter();
        });
        this.syncListGutter();

        // 필터 영역(opt 버튼·그룹·인풋·상단 버튼) 이벤트 위임. 블레이드엔 data-act 만 있다.
        $j.F.Order.SearchOpt.bindFilters(this);

        // 업종 셀렉트의 '기종 상세검색' → 3단 모달, 뱃지 ✕ → 해제
        $j.F.Order.CarDetail.bind(this);

        // 본사/지사/센터 연동 ($j.F.Admin.Util 헬퍼) — 상위 변경 시 doSearch 자동 실행
        $j.Common.Form.registerBonsaHandler('x', 'y', 'z', () => self.doSearch());
        $j.Common.Form.registerGisaHandler('y', 'z', () => self.doSearch());
        $j.Common.Form.updateBonsaSelectBox('', 'x', 'y', 'z');

        // 데스크톱 한정 mobiscroll 데이트피커 (모바일은 기본 input 사용)
        if (!$j.Common.Util.isMobile()) {
            $('#d1,#d2').mobiscroll().datepicker({
                controls: ['calendar'],
                dateFormat: 'YYYY-MM-DD',
                locale: mobiscroll.localeKo,
                buttons: [],  // 설정/확인 버튼 제거 (즉시 반영)
            });
        }

        // '신규접수' 검색창용 자동완성
        $j.F.Admin.AutoComplete.init({
            id: 'find-user-kwd', v: '', d: '',
            onSelect: function (id, $li) {
                // 선택된 회원의 발주 페이지로 이동 (신규 접수)
                $j.F.Admin.Util.userBal($li.data('num'));
                // 검색창 닫기 + 입력값 초기화
                self.hideFindUser();
                $('#find-user-kwd').val('');
            },
            // 행 모양은 발주자명/수주자명 자동완성과 같게 맞춘다 (같은 모듈·같은 데이터인데
            // 여기만 '폰번호 이름' 순서라 딴 화면처럼 보였다) — 제재딱지 + 이름#번호 + 회색 폰번호
            onRender: function (row) {
                const tpl = `<li class="ac-list-group-item" data-num="{num}" data-name="{mem_name}">{card}{mem_name}#{num} <span style="color:#888">{mem_phone}</span></li>`;
                row['card'] = $j.F.Admin.Util.penaltyCard(row['penalty']);
                return Util.render_template(tpl, row);
            },
        });

        // 발주자명(wb)/수주자명(ws) 자동완성 — 고르면 '이재일#2831' 이 인풋에 그대로 들어간다.
        // 목록도 같은 표기로 그려서 "고르면 무엇이 입력되는지" 가 미리 보이게 한다.
        ['wb', 'ws'].forEach((id) => {
            $j.F.Admin.AutoComplete.init({
                id, v: id === 'wb' ? this.initialWb : this.initialWs, d: '',
                // 발주자·수주자 모두 소속자까지 찾는다 (둘은 대칭)
                params: { withSosok: 1 },
                onSelect: function (acId, $li) {
                    $j.F.Order.SearchOpt.setMemberInput(acId, $li.data('name'), $li.data('num'));
                },
                // 이름만 친 상태에서 포커스가 빠져도 번호로 바꾸지 않는다. 이름검색은 그 자체로
                // 유효한 상태이고, 화면에 보이는 값과 실제 검색기준이 늘 같아야 하기 때문이다.
                onFind: function () {},
                onRender: function (row) {
                    // 소속자는 '소유자(소속자)' — 고르면 인풋에도 이 표기가 #번호와 함께 들어간다
                    if (row.mem_permit === '소속자') row.mem_name = `${row.owner_name || ''}(${row.mem_name})`;
                    const tpl = `<li class="ac-list-group-item" data-num="{num}" data-name="{mem_name}">{card}{mem_name}#{num}</li>`;
                    row['card'] = $j.F.Admin.Util.penaltyCard(row['penalty']);
                    return Util.render_template(tpl, row);
                },
            });
        });

        // 타이핑으로 #번호를 지우면 즉시 이름검색 표시로 돌아간다 (표시 = 검색기준)
        $('#wb, #ws').on('input', () => $j.F.Order.SearchOpt.markMemberInputs());
        $j.F.Order.SearchOpt.markMemberInputs();

        // 현장/메모 칸 = 오더 통합검색. 누르면 무엇이 검색되는지 펼쳐 보여준다 (안내 전용, 입력은 이 칸에서).
        $j.F.Order.SearchHelp.init({
            isAdmin: true,                       // 관리메모는 관리자 화면에서만 안내
            onChange: () => self.checkSerKeys(), // 값이 바뀌면 인풋 배경색(기본값과 다름) 갱신
        });
    }

    // ==================== 검색 / 필터 ====================
    /**
     * 현재 UI 상태로 테이블을 새로고침한다. (검색 버튼 onclick 진입점)
     *
     * 발주자/수주자는 인풋 값 자체가 검색기준('이재일#2831')이라 여기서 따로 동기화할 게 없다.
     * 그래서 doSearch 를 거치지 않는 reload(정렬·페이징·수주대기·환원예정·지정미확인)에서도
     * 화면에 보이는 값과 실제 검색조건이 항상 일치한다.
     */
    doSearch() {
        $('input[name=opt]').val(this.makeSearchOpt());
        this.$table.ajax.reload();
    }

    /** on 상태인 opt_* 버튼의 suffix 를 이어붙인 필터 문자열 (예: "ADR"). */
    makeSearchOpt() {
        return $j.F.Order.SearchOpt.make();
    }

    /**
     * '환원예정' 버튼 — 수수료 환원이 예정된 오더만(특수조건 w=1).
     *
     * 나머지 셋(수주대기·지정미확인·환원보류)과 달리 **머무는 모드가 아니다.** 임무는 환원 조회
     * 한 벌을 화면에 찍어주는 데까지다 — 필요한 opt 만 on, w=1, 본사=장비불러, 작업일 기준
     * 3개월 전 달. 그 뒤 CS 가 옵션버튼을 하나라도 건드리면 거기서 임무를 마치고 표시(.on·칩)를
     * 내린다(releaseWhanMode). 찍어준 필터는 시작점이지 붙잡아둘 것이 아니기 때문이다 —
     * 환원 대상을 띄워놓고 "수수료 15% 만" 처럼 좁혀 보는 게 실제 사용법이다.
     *
     * 표시가 내려가도 w=1 과 '환원 문자발송' 버튼은 남는다. 조합을 바꿔도 보고 있는 것은 여전히
     * 환원예정 범위이고, 그 안에서 문자를 보내는 것이 이 화면의 목적이다. 범위까지 걷어내려면
     * 이 버튼을 다시 눌러 프리셋을 처음부터 찍거나, 다른 프리셋 버튼으로 넘어가면 된다
     * (수주대기·지정미확인·환원보류가 각자 자기 w 로 덮어쓴다).
     */
    checkWhan() {
        // 프리셋 그대로인 동안(모드 on) 다시 누르면 접수목록(초기설정)으로 복원.
        // 옵션버튼을 건드려 이미 모드가 풀렸다면 여기로 오지 않고 프리셋을 다시 찍는다.
        if (this.listMode === 'whan') { this.exitPreset(); return; }

        // 환원예정 모드 진입
        this.toggleAllOpt(false);
        ['A','S','T','U','R','B','E','I','J','X','Y','L','M','N','O','P','H'].forEach((o) => $('#opt_' + o).addClass('on'));

        this.dateType = 'd';
        $('#topchbox_a').html('작업일기준');
        $('input[name=dt]').val('d');
        $('#d1').val(this.whan_start_dt);
        $('#d2').val(this.whan_end_dt);
        this.presetDates = true; // 이 기간은 프리셋이 써넣은 것 (벗어날 때 되돌린다)
        this.specialCond = '1';
        $('input[name=w]').val('1');
        $('#x').val('012'); // 본사:장비불러 (g5_group.code 는 본사=3자리 '012'. '12' 로는 option 매칭 실패→order_sosok 조건 누락)
        this.setListMode('whan'); // 다른 프리셋 표시를 끄고 이 모드로
        this.$table.ajax.reload();
    }

    /**
     * '지난달 환원' 버튼 — 직전 1일 배치가 환원한 달의 **환원완료 건**을 한 벌 찍어준다.
     *
     * 나머지 프리셋(수주대기·지정미확인·환원보류·환원예정)과 성격이 다르다 — 이건 **모드가 아니다.**
     * 숨은 특수조건(w)도, 칩("…만 보는 중")도 없다. 보이는 토글옵션만 지난달 환원완료를 보기 좋게
     * 깔아주고 끝낸다(환원예정은 반대로 w=1 과 문자발송 버튼을 숨겨 남긴다 — 그쪽은 그게 임무라서다).
     * 그래서 CS 가 그 위에서 수수료율·진행상태를 바꿔 보면 화면은 거짓말을 하지 않는다 — 걸린 숨은
     * 조건이 없으니 토글이 곧 지금 보고 있는 전부다.
     *
     * 찍는 토글(= 보이는 필터 그대로가 질의다):
     *   배차유형 어플·지정 / 책임 싸인 / 수수료 '환원'만 / 작업시간 일·오전·오후·시간·야간 / 진행상태 작업완료·수금완료
     * 수수료에서 '환원'(opt_Y)만 켜고 요율(0/10/15/16+)을 끄면, 서버 수수료절이 요율칸을 -1 센티넬로
     * 떨어뜨려 결국 whan_status=2(환원완료)된 건만 남는다 (OrderListService 수수료 블록). 즉 이 버튼은
     * "지난달 환원이 끝난 건들"을 보여준다 — 월초에 눌러 환원이 잘 됐나 되짚는 용도.
     * 기간은 직전 환원 달(이번달 -3개월), 본사=장비불러(x='012'), 작업일 기준.
     */
    checkWhanDone() {
        this.setListMode(''); // 다른 프리셋 모드가 켜져 있었으면 표시·칩·문자버튼을 내린다 (이건 모드가 아니다)
        this.toggleAllOpt(false);
        ['A','B','E','Y','L','M','N','O','P','U','V'].forEach((o) => $('#opt_' + o).addClass('on'));

        this.dateType = 'd';
        $('#topchbox_a').html('작업일기준');
        $('input[name=dt]').val('d');
        $('#d1').val(this.whan_done_st);
        $('#d2').val(this.whan_done_en);
        this.presetDates = true; // 이 기간은 버튼이 써넣은 것 (다른 프리셋으로 넘어갈 때 되돌린다)
        this.specialCond = ''; // 숨은 특수조건 없음 (w 미사용)
        $('input[name=w]').val('');
        $('#x').val('012'); // 본사:장비불러 (3자리 '012' 라야 order_sosok 조건이 선다)
        this.$table.ajax.reload();
    }

    /**
     * '환원예정 문자발송' 버튼 — 환원예정 대상자에게 결제확인 문자 일괄발송 모달 오픈.
     * 모달 본체(마크업/로직)는 WhanSmsModal 이 담당하며 lazy 로 1회만 인스턴스화한다.
     */
    openWhanSms() {
        if (!this.whanSms) this.whanSms = new WhanSmsModal(this);
        this.whanSms.open();
    }

    /**
     * 상세 펼침의 '단계분포' 버튼 — 매칭 회원의 단계(lvl)별 인원수 모달.
     *
     * 매칭리스트 화면(/adm/order/match/list)이 띄우는 것과 **같은 모달·같은 데이터**다.
     * 그쪽은 페이지를 그릴 때 이미 들고 있지만 여기선 눌렀을 때만 받아온다 — 상세는 행마다
     * 펼쳐지는 것이라, 열 때마다 분포까지 계산하면 안 볼 사람 몫까지 매번 치른다.
     *
     * @param {number} ordernum 오더번호
     */
    openMatchLevels(ordernum) {
        $.get('/adm/ajax/order/match/levels', { ordernum }, (d) => {
            if (!d || !d.ok) { alert((d && d.msg) || '매칭 정보를 불러올 수 없습니다.'); return; }
            $j.Ui.JBModal.target('match-level-chart-modal', {
                target: '$j.F.Admin.Order.MatchLevelChart',
                data: {
                    stats:     d.levelStats || [],
                    levelAll:  d.levelAll   || [],
                    matchType: d.matchType  || '',
                    ver:       d.matchVer   || '',
                },
                title: `단계분포 — 오더 ${ordernum}`,
                width: '980px',
            });
        }, 'json');
    }

    /**
     * 현재 검색 UI 상태를 서버 페이로드로 직렬화 (DataTables ajax.data 와 동일 규약).
     * 환원예정 문자발송 목록(whanSmsList)이 접수목록과 같은 필터를 쓰도록 공유한다.
     */
    currentSearchParams() {
        return $j.F.Order.SearchOpt.params({
            dateType: this.dateType,
            specialCond: this.specialCond,
            sortField: this.sortField,
            sortOrder: this.sortOrder,
        });
    }

    // ==================== 목록 프리셋 (수주대기·지정미확인·환원보류·환원예정) ====================
    /**
     * 프리셋 버튼의 활성표시를 한 곳에서 맞춘다 — 켜진 하나만 .on.
     *
     * 이 넷은 상단줄의 액션 버튼과 달리 "누르면 목록이 통째로 바뀌는" 모드라 상태가 있다.
     * 눌러도 버튼이 그대로면 지금 보고 있는 게 접수목록인지 수주대기인지 화면에 단서가 없다.
     *
     * 켜지는 방식은 넷이 똑같다 — .on(회색) + 칩. 예전엔 환원예정만 라벨이 '접수목록'(빨강)으로
     * 뒤집혔는데, 나란히 선 버튼 중 하나만 글자가 바뀌면 그게 무슨 뜻인지(지금 상태인지 다음에
     * 갈 곳인지) 매번 다시 읽어야 한다.
     *
     * 꺼지는 방식만 환원예정이 다르다 — 셋은 해제할 때까지 머물고, 환원예정은 옵션버튼 한 번에
     * 스스로 내려간다(releaseWhanMode).
     *
     * @param {string} mode '' | 'wait' | 'jijung' | 'undone' | 'whanhold' | 'whan'
     */
    setListMode(mode) {
        this.listMode = mode;
        const ID = { wait: '#btn_wait', jijung: '#btn_jijung', undone: '#btn_undone', whanhold: '#btn_whanhold', whan: '#topchbox_b' };
        $('#btn_wait, #btn_jijung, #btn_undone, #btn_whanhold, #topchbox_b').removeClass('on');
        if (ID[mode]) $(ID[mode]).addClass('on');
        // 문자발송은 환원예정에서만 쓰는 버튼이라 그 모드에서만 보인다
        $('#whanSmsBtn').prop('hidden', mode !== 'whan');
        this.renderModeChip();
    }

    /**
     * 지금 걸려 있는 특수조건을 버튼줄 옆에 칩으로 띄운다 (없으면 지운다).
     * 버튼의 회색 on 만으로는 "무슨 조건이 걸렸는지" 가 아니라 "이 버튼을 눌렀다" 까지만 보인다 —
     * 필터칸을 아무리 봐도 whan_status 조건은 안 나오므로 문장으로 한 줄 남긴다.
     */
    renderModeChip() {
        const label = OrderList.MODE_LABEL[this.listMode];
        $('#orderlistinfos').html(label
            ? '<span class="list-mode-chip">' + label + '만 보는 중'
              + '<b data-act="preset-clear" title="해제 — 접수목록으로 돌아갑니다">&times;</b></span>'
            : '');
    }

    /**
     * 환원예정 모드를 스스로 내린다 — 옵션버튼(opt_*·전체선택·그룹)을 건드리는 순간.
     *
     * 이 넷 중 환원예정만 성격이 다르다. 수주대기·지정미확인·환원보류는 "그것만 보기" 라 해제할
     * 때까지 붙잡고 있어야 뜻이 맞지만, 환원예정은 조회 한 벌을 찍어주는 게 임무의 전부다.
     * 그런데 .on 과 칩("환원예정만 보는 중")이 계속 붙어 있으면 사용자가 수수료·진행상태를 바꿔
     * 다른 조합을 보는 중에도 화면은 "프리셋 그대로" 라고 말한다 — 그 순간부터 칩은 거짓이다.
     *
     * 내리는 것은 표시(.on·칩)와 모드값뿐이다. specialCond(w=1)·'환원 문자발송' 버튼·기간은
     * 그대로 둔다 — 조합을 바꿔도 보고 있는 범위는 여전히 환원예정이고, 여기서 문자를 보내는
     * 것이 이 화면의 목적이라 버튼이 사라지면 조합을 좁힌 대가로 기능을 잃는다.
     */
    releaseWhanMode() {
        if (this.listMode !== 'whan') return;
        this.listMode = '';
        $('#topchbox_b').removeClass('on');
        this.renderModeChip();
    }

    /**
     * 프리셋이 바꿔놓은 기간(기준 + 범위)을 처음 값으로 되돌린다.
     *
     * 환원예정(3개월 전 달)·환원보류(작업일 14개월)는 자기 기간을 화면에 써넣는다.
     * 그 상태에서 기간을 손대지 않는 프리셋(수주대기·지정미확인)으로 넘어가면 남의 기간이
     * 그대로 따라붙어 "왜 이 기간이지?" 가 된다. 프리셋 해제(exitPreset)는 필터 전체를
     * 복원하니 문제가 없고, 프리셋끼리 갈아탈 때만 이게 필요하다.
     */
    restorePresetDates() {
        if (!this.presetDates || !this._initFilters) return;
        const s = this._initFilters;
        $('#d1').val(s.d1);
        $('#d2').val(s.d2);
        this.dateType = s.dateType;
        $('input[name=dt]').val(s.dt);
        $('#topchbox_a').html(s.topA);
        this.presetDates = false;
    }

    /** 프리셋 해제 — 페이지를 처음 열었을 때의 필터로 되돌리고 접수목록으로 복귀. */
    exitPreset() {
        this.restoreInitialFilters();
        this.setListMode('');
        this.$table.ajax.reload();
    }

    /** '수주대기' 버튼 — 수주대기 관련 상태만 필터링. 작업일 기준으로 전환. */
    checkWait() {
        if (this.listMode === 'wait') { this.exitPreset(); return; }
        this.restorePresetDates(); // 환원예정·환원보류에서 넘어왔으면 그 기간을 털어낸다
        this.dateType = 'd';
        $('input[name=dt]').val('d');
        this.toggleAllOpt(false);
        ['A','R','C','B','E','D','F','I','J','X','Y','L','M','N','O','P','Q','H'].forEach((o) => $('#opt_' + o).addClass('on'));
        this.specialCond = '';
        $('input[name=w]').val('');
        this.setListMode('wait');
        this.$table.ajax.reload();
    }

    /**
     * '환원보류' 버튼 — 환원을 보류해둔 오더만(whan_status=1, 특수조건 w=3).
     *
     * 필터그룹의 버튼으로 두지 않은 이유: 그 버튼들은 켠 것끼리 OR 로 결과를 넓히는데 보류는
     * "이것만 보기" 라 성격이 반대다. 수주대기·지정미확인과 같은 부류라 상단 독립버튼으로 뺐다.
     * 기간은 넓게(작업일 기준 14개월) 잡는다 — 보류는 몇 달씩 묵어서 기본 12일 범위로는 안 걸린다.
     */
    checkWhanHold() {
        if (this.listMode === 'whanhold') { this.exitPreset(); return; }
        this.dateType = 'd';
        $('#topchbox_a').html('작업일기준');
        $('input[name=dt]').val('d');
        $('#d1').val(this.whan_hold_st);
        $('#d2').val(this.whan_hold_en);
        this.presetDates = true; // 이 기간은 프리셋이 써넣은 것 (벗어날 때 되돌린다)
        this.toggleAllOpt(true); // 보류 조건 하나로만 좁힌다 — 다른 그룹은 전부 열어둔다
        this.specialCond = '3';
        $('input[name=w]').val('3');
        this.setListMode('whanhold');
        this.$table.ajax.reload();
    }

    /**
     * '작업미완료' 버튼 — 끝나는 날이 지났는데 완료를 안 누른 오더(특수조건 w=4). CS 가 전화하는 목록이다.
     *
     * 환원보류처럼 조건 하나로만 좁히므로 필터는 전부 켠다. 기간은 어제~오늘로 찍어주고 기준은 작업일로
     * 바꾼다 — 오더생성기준이면 며칠 전에 접수한 어제 작업이 빠진다. 더 묵은 건은 기간칸을 넓혀 조회한다.
     * 기간 시작일보다 먼저 시작한 장기 작업도 시작일을 앞당겨야 보인다.
     */
    checkUndone() {
        if (this.listMode === 'undone') { this.exitPreset(); return; }
        this.dateType = 'd';
        $('#topchbox_a').html('작업일기준');
        $('input[name=dt]').val('d');
        $('#d1').val(this.undone_st);
        $('#d2').val(this.undone_en);
        this.presetDates = true; // 이 기간은 버튼이 써넣은 것 (다른 프리셋으로 넘어갈 때 되돌린다)
        this.toggleAllOpt(true);
        this.specialCond = '4';
        $('input[name=w]').val('4');
        this.setListMode('undone');
        this.$table.ajax.reload();
    }

    /**
     * '지정미확인' 버튼 — 지정발주 중 수주자가 확인하지 않은 오더(특수조건 w=2).
     *
     * 필터를 전부 켜고 들어간다. 서버가 이 조건을 다른 필터 위에 **얹기만** 하므로(예전엔
     * 통째로 덮어써서 필터칸이 먹통이었다), 전부 꺼둔 채로 들어가면 수수료 조건이 0건으로 만든다.
     * 켜고 들어가면 첫 목록은 예전과 같고, 그 뒤 사용자가 버튼으로 좁히는 것이 실제로 먹는다.
     */
    jijungmi() {
        if (this.listMode === 'jijung') { this.exitPreset(); return; }
        this.restorePresetDates(); // 환원예정·환원보류에서 넘어왔으면 그 기간을 털어낸다
        this.toggleAllOpt(true);
        this.specialCond = '2';
        $('input[name=w]').val(this.specialCond);
        this.setListMode('jijung');
        this.$table.ajax.reload();
    }

    /**
     * 기간 검색 기준 토글 (오더생성 ↔ 작업일).
     * 라벨과 내부 상태(dateType), hidden input(dt) 을 함께 갱신.
     */
    toggleOdrDate() {
        if (this.dateType === 'd') {
            this.dateType = 'n';
            $('input[name=dt]').val('n');
            $('#topchbox_a').html('오더생성기준');
        } else {
            this.dateType = 'd';
            $('input[name=dt]').val('d');
            $('#topchbox_a').html('작업일기준');
        }
    }

    // ==================== 버튼 토글 ====================
    /**
     * opt_* 버튼 on/off 토글.
     * id 가 'optall' 인 버튼만 특수 처리(rxon/rxoff 클래스 + 전체 토글, 라벨은 전체선택/선택취소).
     * @param {HTMLElement} tar 클릭된 버튼 엘리먼트
     */
    toggleButton(tar) {
        this.releaseWhanMode(); // 환원예정은 여기까지가 임무다
        $j.F.Order.SearchOpt.toggleButton(tar);
    }

    /**
     * 그룹 체크박스(tabChbox{n}) 상태로 해당 그룹의 opt 버튼 전체 on/off.
     * @param {number} n 그룹 인덱스 (1=배차유형, 2=책임, 3=수수료, 4=작업시간, 5=진행상태)
     */
    toggleGroup(n) {
        this.releaseWhanMode();
        $j.F.Order.SearchOpt.toggleGroup(n);
    }

    /**
     * 모든 opt_* 버튼과 그룹 체크박스를 동일 상태로 세팅.
     * 프리셋(checkWhan/checkWait/…)이 필터를 찍을 때 쓰는 내부 경로라 releaseWhanMode 를 부르지
     * 않는다 — 여기서 부르면 프리셋이 방금 켠 모드를 자기 손으로 도로 끄게 된다.
     */
    toggleAllOpt(on) {
        $j.F.Order.SearchOpt.toggleAll(on);
    }

    // ==================== 검색 UI ====================
    /** 인풋 값 초기화 — 클릭하면 비운다 (blade: data-clear-on-click). 구 CCC(). */
    clearInput(tar) {
        $j.F.Order.SearchOpt.clearInput(tar);
        this.checkSerKeys();
    }

    /**
     * 필터 인풋들의 배경색을 "기본값과 다른가?" 기준으로 갱신한다.
     * 회색(#B5B5B5) = 값이 바뀌었다는 시각적 힌트. 흰색(#FFFFFF) = 기본값.
     */
    checkSerKeys() {
        $j.F.Order.SearchOpt.checkSerKeys(this.defDay_st, this.defDay_en,
            ['c', 'x', 'y', 'z', 'stx', 'n', 'wb', 'ws']);
    }


    // ==================== 상단 버튼 (레거시 Call_main 디스패치) ====================
    /**
     * 상단 유틸 버튼의 공통 진입점. 숫자 코드 → 해당 기능 디스패치.
     * (레거시 switch-case 스타일 유지. 향후 각 버튼에 명시적 onclick 부여하며 제거 예정.)
     * @param {number} n 1=일괄수금완료, 4=푸시알림, 6=문자접수 (5=작업확인서는 접수목록에서 뺐다 — 오더상세에서 연다)
     */
    callMain(n) {
        if (n === 1) this.doPaydone();
        else if (n === 4) this.sendPush();
        else if (n === 6) window.open('/adm/sms-recv');
    }

    /**
     * 현재 화면의 오더들을 일괄 수금완료로 전환한다.
     * 2단계 confirm + 버튼 락으로 중복 실행/오클릭을 방지. 수금완료는 되돌릴 수 없음.
     */
    doPaydone() {
        if (this.btnPaydoneCheck()) { alert('현재 처리중입니다'); return; }
        const str = $('#orderNoList').val();
        const arr = str.split(',');
        if (!confirm(arr.length + '건의 오더건에 대하여 수금완료 처리하시겠습니까?')) return;
        if (!confirm('수금완료처리되면 이전 상태로 되돌릴수 없으니 신중하게 처리해주세요.\n정말 수금완료 처리하시겠습니까?')) return;

        this.btnPaydoneDisable();
        $.ajax({
            type: 'POST',
            url: '/adm/ajax/order/pay-done-all',
            data: { orderNoList: str },
            success: (r) => {
                this.btnPaydoneEnable();
                // 상태(수금완료)는 바로 바뀌고, 수수료·포인트·알림은 백그라운드에서 이어서 처리된다
                const skip = r && r.skipped ? `\n(작업완료가 아니어서 건너뛴 오더 ${r.skipped}건)` : '';
                alert(`${r ? r.claimed : 0}건을 수금완료로 바꿨습니다.\n수수료·포인트·알림은 잠시 뒤 이어서 처리됩니다.${skip}`);
            },
            error: () => {
                this.btnPaydoneEnable();
                alert('처리하지 못했습니다. 목록을 새로고침한 뒤 다시 확인해 주세요.');
            },
        });
    }

    // 일괄수금완료 버튼 상태 제어 (value 값 자체를 plug 로 사용 → 중복 클릭 방지)
    /** 처리 중이면 true. 버튼 value 가 '일괄수금완료' 가 아니면 처리 중으로 간주. */
    btnPaydoneCheck()   { return $('#btn_paydone').val() !== '일괄수금완료'; }
    /** 처리중 상태로 락 (빨강 배경 + '처리중...' 라벨). */
    btnPaydoneDisable() { $('#btn_paydone').css('backgroundColor', 'red').val('처리중...'); }
    /** 락 해제하고 원래 라벨로 복구. */
    btnPaydoneEnable()  { $('#btn_paydone').css('backgroundColor', '#ffffff').val('일괄수금완료'); }

    /**
     * 푸시/불러톡 발송 모달 열기.
     * 현재 테이블의 listMemNoLIst 를 서버로 보내 대상 회원 목록 HTML 을 받고 모달로 표시.
     */
    sendPush() {
        const data = $('#listMemNoLIst').val();
        if (!data) { alert('선택된 오더가 없습니다.'); return; }
        $.ajax({
            type: 'POST',
            url: '/adm/ajax/order/push-send',
            data: { listMemNoLIst: data },
            success: function (html) {
                $j.Ui.JBModal.html('jb-modal-push-send', html, '푸시/불러톡 발송', { width: '900px', onOpen: () => $j.F.Admin.OrderPushSend.init() });
            },
        });
    }

    /**
     * 엑셀 다운로드 — 목록 조회와 **같은 검색 파라미터**를 들고 가서 서버가 같은 SQL 을 LIMIT 없이
     * 다시 돌린다(Excel\OrderController → OrderListService::listSql). 받는 것은 화면의 한 페이지가
     * 아니라 지금 검색조건의 전 건이다. 세션에 남긴 SQL 에 기대지 않는다 — 동시에 끝난 다른 ajax 가
     * 세션을 덮어써 '먼저 조회를 실행해주세요' 가 떴다.
     */
    saveToExcel() {
        document.location.href = '/excel/order?' + $.param(this.currentSearchParams());
    }

    // ==================== 신규접수 회원 찾기 UI ====================
    /**
     * '신규접수' 버튼 아래 검색 팝오버 토글.
     * 열려있는 상태에서 버튼을 다시 누르면 닫힌다 (같은 버튼이 여닫이 노릇을 한다).
     */
    showFindUser() {
        const $pop = $('#find-user');
        if ($pop.hasClass('is-open')) { this.hideFindUser(); return; }

        $pop.addClass('is-open');
        this.placeFindUser();
        $('#find-user-kwd').val('').focus();

        // 바깥 클릭·ESC 로 닫는다. 예전엔 닫는 방법이 '회원 선택' 뿐이라 한번 열면 계속 떠 있었다.
        // ⚠ 자동완성 목록(#find-user-kwd-ul-autocomplete)은 모달 클리핑 때문에 <body> 직속으로
        //   떠 있어 #find-user 의 자손이 아니다 — 빼먹으면 목록 항목을 누르는 mousedown 순간
        //   "바깥 클릭"으로 오인해 여기서 먼저 패널을 닫아버려서, 뒤이은 click(선택) 이 발주창을
        //   못 연다. 이름검색 후 이름을 눌러도 아무 반응이 없던 것이 이 때문이었다.
        $(document)
            .on('mousedown.findUser', (e) => {
                if ($(e.target).closest('#find-user, #new-register, #find-user-kwd-ul-autocomplete').length) return;
                this.hideFindUser();
            })
            .on('keydown.findUser', (e) => {
                if (e.key === 'Escape') this.hideFindUser();
            });

        // 필터가 가로로 스크롤되면 버튼이 움직인다 — 패널도 따라가야 한다.
        // scroll 은 버블링하지 않아 **캡처**로 받는다 (필터 상자 같은 내부 스크롤까지 잡으려고).
        this._onFindUserMove = () => this.placeFindUser();
        document.addEventListener('scroll', this._onFindUserMove, true);
        window.addEventListener('resize', this._onFindUserMove);
    }

    /**
     * 패널을 '신규접수' 버튼 아래에 화면좌표로 붙인다.
     * 버튼 기준 absolute 로 두면 검색필터의 가로스크롤 상자(#orderFilterScroll)에 잘린다.
     */
    placeFindUser() {
        const btn = document.getElementById('new-register');
        if (!btn) return;
        const r = btn.getBoundingClientRect();
        $('#find-user').css({ top: r.bottom + 6, left: r.left });
    }

    /** 검색 팝오버를 닫는다 (선택 완료·✕·바깥클릭·ESC). */
    hideFindUser() {
        $('#find-user').removeClass('is-open');
        $('#find-user-kwd').val('');
        $('#find-user-kwd-ul-autocomplete').hide();   // 자동완성 목록도 함께 접는다
        $(document).off('.findUser');

        if (this._onFindUserMove) {
            document.removeEventListener('scroll', this._onFindUserMove, true);
            window.removeEventListener('resize', this._onFindUserMove);
            this._onFindUserMove = null;
        }
    }

    // ==================== 오더 상세 / 팝업 (공용 모듈 위임) ====================
    // 실제 구현은 $j.F.Order.ListTable (협력사 /p/orders 와 공용).
    // 여기서는 블레이드 인라인 onclick / 외부창 브리지가 참조하는 이름만 유지한다.

    /** 오더 행 아래에 상세 정보 tr 삽입 (토글). */
    openTab(name, smode, ordernum, mb_no) {
        $j.F.Order.ListTable.openTab(name, smode, ordernum, mb_no);
    }

    /** 외부 창/iframe 이 현재 열린 상세탭 HTML 을 교체 (레거시 parent.orderDetail_show). */
    orderDetailShow(val) {
        $j.F.Order.ListTable.orderDetailShow(val);
    }

    /** 오더번호 컬럼 클릭 — 오더 핸들링 팝업. */
    detailOrderOpen(num) {
        $j.F.Order.ListTable.detailOrderOpen(num);
    }

    /** '업종(유형)' 컬럼 클릭 — 매칭내역(SMS 발송 통합 UI) 팝업. */
    evtNoticeShow(num) {
        $j.F.Order.ListTable.evtNoticeShow(num);
    }

    /** '현장주소' 컬럼 클릭 — 지도 팝업. */
    showMap(pos1, pos2) {
        $j.F.Order.ListTable.showMap(pos1, pos2);
    }

    /** 작업사진 팝업 (행 확장 detail.blade.php 썸네일 클릭). 작업확인서는 1차만 본다 */
    showPic(orderno, n) {
        $j.F.Order.ListTable.showPic(orderno, n, 1);
    }

    /** 현재 펼쳐진 상세탭의 오더번호 (외부창 호환용 getter). */
    get current_ordernum() {
        return $j.F.Order.ListTable.currentOrdernum();
    }

    /**
     * 오더 홀드 설정 — 특정 수주자에게 미리 배정해두기. (관리자 전용)
     * @param {number} ordernum 오더번호
     */
    reservOrder(ordernum) {
        const mb_no = document.getElementById('reserv_set_' + ordernum).value;
        if (isNaN(parseInt(mb_no, 10))) { alert('회원 번호를 숫자만으로 입력해 주세요'); return; }

        if (!confirm(`이 오더 [ ${ordernum} ] 을(를) 홀드상태로 설정할까요?\n\n이 오더를 수주할 회원번호를 아시면회원번호를 입력하시고\n모르시면[999999] 로 입력하시면 됩니다.\n\n이 후 지정할 사람을 변경하려면다시 회원번호로 변경해 주시면 됩니다.`)) return;

        const self = this;
        $.ajax({
            type: 'post',
            url: `/adm/ajax/order/${ordernum}/hold`,
            data: { userNum: mb_no },
            success: function (data) {
                if (!data.result) { Util.swalert(data.message); return; }
                // 페이지 전체 새로고침 대신 DataTables 만 갱신 (페이지 위치 유지)
                self.$table && self.$table.ajax.reload(null, false);
            },
        });
    }

    /**
     * 외부 창에서 오더 목록 정보를 주입할 때 사용되는 레거시 훅.
     * @param {Array} orderList  각 요소는 { onum, ... } 형태
     */
    updateOrderDb(orderList) {
        orderList.forEach((row) => { this.order_db[row.onum] = row; });
    }

    // ==================== 유틸 ====================
    /**
     * 템플릿 문자열에 삽입하기 위한 간단한 이스케이프.
     * `<a onclick="openTab('${name}',...)">` 같은 문자열 조립에서 따옴표 깨짐 방지용.
     * XSS 방어 용도로는 부족하므로 신뢰할 수 없는 텍스트엔 escapeHtml 사용할 것.
     */
    escapeStr(s) {
        return (s || '').replace(/'/g, "\\'").replace(/"/g, '&quot;');
    }

    /**
     * HTML 엔티티 이스케이프 — 브라우저 내장 textNode 를 이용해 안전하게 변환.
     * 사용자 입력 문자열(현장주소, 메모 등)을 innerHTML 로 삽입할 때 사용한다.
     */
    escapeHtml(s) {
        const d = document.createElement('div');
        d.appendChild(document.createTextNode(s || ''));
        return d.innerHTML;
    }
}
